import React from "react";
import { Text, View } from "react-native";

/**
 * Counts above this render as "99+" rather than growing the pill without bound.
 * The unread count itself is not clamped — only its label.
 */
export const BADGE_MAX_COUNT = 99;

export const formatBadgeCount = (count: number): string =>
  count > BADGE_MAX_COUNT ? `${BADGE_MAX_COUNT}+` : `${count}`;

type NotificationBadgeProps = Readonly<{
  count?: number;
  /**
   * "overlay" pins the badge to the top-right of an icon (collapsed nav).
   * "inline" sits it in normal flow beside a label (expanded nav).
   */
  variant?: "overlay" | "inline";
}>;

/**
 * Unread count pill. Renders nothing at zero — an empty badge reads as a bug,
 * and a zero-count badge is noise on every screen with no notifications.
 */
export const NotificationBadge = ({
  count = 0,
  variant = "inline",
}: NotificationBadgeProps) => {
  if (!count || count <= 0) return null;

  const label = formatBadgeCount(count);

  return (
    <View
      accessibilityLabel={`${count} unread notifications`}
      className={[
        "items-center justify-center rounded-full bg-error px-1",
        // Keeps a single digit circular rather than a narrow oval.
        "h-[18px] min-w-[18px]",
        variant === "overlay" ? "absolute -right-1.5 -top-1.5" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Text className="text-[10px] font-semibold leading-none text-white">
        {label}
      </Text>
    </View>
  );
};

export default NotificationBadge;
